import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  MapPin, 
  Phone, 
  Mail, 
  Globe, 
  Clock, 
  Star,
  DollarSign,
  Shield,
  User,
  Calendar,
  Car,
  Heart,
  Home,
  Award,
  Stethoscope,
  Brain,
  Accessibility
} from "lucide-react";
import { Link } from "react-router-dom";

import { ExtendedDirectoryEntry } from '@/types/caregiver';

type DirectoryEntry = ExtendedDirectoryEntry;

interface ProfessionalCardProps {
  professional: DirectoryEntry;
}

export function ProfessionalCard({ professional }: ProfessionalCardProps) {
  const fullName = `${professional.first_name} ${professional.last_name}`;
  const isCaregiver = professional.profession === 'Independent Caregiver';
  
  const getAvailabilityStatus = () => {
    if (professional.detailed_availability) {
      const hasAvailability = Object.values(professional.detailed_availability).some(hours => hours);
      return hasAvailability ? 'Available' : 'Contact for availability';
    }
    return professional.availability || 'Contact for availability';
  };

  const getServiceAreaDisplay = () => {
    if (professional.service_radius_miles && professional.service_city) {
      return `${professional.service_city} (${professional.service_radius_miles} mi radius)`;
    }
    if (professional.service_areas?.length > 0) {
      return professional.service_areas.join(', ');
    }
    return 'Contact for service area';
  };

  const getConsultationRate = () => {
    if (isCaregiver && professional.hourly_rate) {
      return `$${professional.hourly_rate}/hr`;
    }
    if (professional.consultation_rates?.initial) {
      return professional.consultation_rates.initial;
    }
    return 'Contact for rates';
  };


  const getCaregiverSkills = () => {
    if (!isCaregiver) return [];
    const skills = [];
    
    if (professional.skills_dementia_care) skills.push({ icon: Brain, label: 'Dementia Care' });
    if (professional.skills_mobility_assistance) skills.push({ icon: Accessibility, label: 'Mobility' });
    if (professional.skills_medication_support) skills.push({ icon: Stethoscope, label: 'Medication' });
    if (professional.skills_adl_iadl?.length) {
      skills.push({ icon: User, label: `ADL/IADL (${professional.skills_adl_iadl.length})` });
    }
    
    return skills.slice(0, 4);
  };

  const getCaregiverCertifications = () => {
    if (!isCaregiver) return [];
    const certs = [];
    
    if (professional.certifications_cpr) certs.push('CPR');
    if (professional.certifications_cna) certs.push('CNA');
    if (professional.certifications_ot_pt_aide) certs.push('OT/PT Aide');
    if (professional.certifications_other?.length) {
      certs.push(...professional.certifications_other.slice(0, 2));
    }
    
    return certs.slice(0, 3);
  };

  const getWorkPreferences = () => {
    if (!isCaregiver) return [];
    const prefs = [];
    
    if (professional.has_transportation) prefs.push({ icon: Car, label: 'Transportation' });
    if (professional.comfortable_with_pets) prefs.push({ icon: Heart, label: 'Pet Friendly' });
    if (professional.willing_housekeeping) prefs.push({ icon: Home, label: 'Housekeeping' });
    
    return prefs;
  };

  const shouldShowExperience = () => {
    return professional.experience_years && 
           professional.experience_years !== "0-1 years" && 
           professional.experience_years.trim() !== "";
  };

  return (
    <Card className="h-full transition-all duration-200 hover:shadow-lg border-border/50 group relative">
      <Link to={professional.directory_slug ? `/professionals/${professional.directory_slug}` : '#'} className="block h-full" onClick={(e) => { if (!professional.directory_slug) e.preventDefault(); }}>
      <CardHeader className="pb-4">
        <div className="flex items-start gap-4">
          {professional.professional_photo_url ? (
            <img
              src={professional.professional_photo_url}
              alt={`${fullName} profile`}
              className="w-16 h-16 rounded-full object-cover border-2 border-border"
            />
          ) : (
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center border-2 border-border">
              <User className="w-8 h-8 text-muted-foreground" />
            </div>
          )}
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <h3 className="text-lg font-semibold text-foreground truncate group-hover:text-primary transition-colors">
                {fullName}
              </h3>
              <Badge variant="secondary" className="shrink-0">
                {professional.profession}
              </Badge>
              
            </div>
            
            {professional.company_name && (
              <p className="text-sm text-muted-foreground mb-1 truncate">
                {professional.company_name}
              </p>
            )}
            
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <MapPin className="w-4 h-4 shrink-0" />
              <span className="truncate">{professional.license_state}</span>
              <Clock className="w-4 h-4 shrink-0 ml-2" />
              <Badge variant="outline" className="text-xs">
                {getAvailabilityStatus()}
              </Badge>
            </div>
          </div>
        </div>

        {professional.bio && (
          <div className="mt-3 p-3 bg-muted/50 rounded-md">
            <p className="text-sm text-muted-foreground line-clamp-3">
              {professional.bio}
            </p>
          </div>
        )}
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Experience and Rates */}
        <div className={`grid gap-3 ${shouldShowExperience() ? 'grid-cols-2' : 'grid-cols-1'}`}>
          {shouldShowExperience() && (
            <div className="flex items-center gap-2">
              <Star className="w-4 h-4 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Experience</p>
                <p className="text-sm font-medium">{professional.experience_years}</p>
              </div>
            </div>
          )}
          <div className="flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-muted-foreground" />
            <div>
              <p className="text-xs text-muted-foreground">{isCaregiver ? 'Hourly Rate' : 'Consultation'}</p>
              <p className="text-sm font-medium">{getConsultationRate()}</p>
            </div>
          </div>
        </div>

        {/* Caregiver Skills */}
        {isCaregiver && getCaregiverSkills().length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium flex items-center gap-2">
              <Award className="w-4 h-4 text-muted-foreground" />
              Skills & Care
            </h4>
            <div className="flex flex-wrap gap-1">
              {getCaregiverSkills().map((skill, index) => (
                <Badge key={index} variant="outline" className="text-xs flex items-center gap-1">
                  <skill.icon className="w-3 h-3" />
                  {skill.label}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Caregiver Certifications */}
        {isCaregiver && getCaregiverCertifications().length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium flex items-center gap-2">
              <Shield className="w-4 h-4 text-muted-foreground" />
              Certifications
            </h4>
            <div className="flex flex-wrap gap-1">
              {getCaregiverCertifications().map((cert) => (
                <Badge key={cert} variant="secondary" className="text-xs">
                  {cert}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Work Preferences */}
        {isCaregiver && getWorkPreferences().length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium">Work Preferences</h4>
            <div className="flex flex-wrap gap-2">
              {getWorkPreferences().map((pref, index) => (
                <div key={index} className="flex items-center gap-1 text-xs text-muted-foreground">
                  <pref.icon className="w-3 h-3" />
                  {pref.label}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Availability Days for Caregivers */}
        {isCaregiver && professional.availability_days?.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium flex items-center gap-2">
              <Calendar className="w-4 h-4 text-muted-foreground" />
              Available Days
            </h4>
            <div className="flex flex-wrap gap-1">
              {professional.availability_days.slice(0, 4).map((day) => (
                <Badge key={day} variant="outline" className="text-xs capitalize">
                  {day.slice(0, 3)}
                </Badge>
              ))}
              {professional.availability_days.length > 4 && (
                <Badge variant="outline" className="text-xs">
                  +{professional.availability_days.length - 4}
                </Badge>
              )}
            </div>
          </div>
        )}

        {/* Service Area */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-medium">Service Area</span>
          </div>
          <p className="text-sm text-muted-foreground pl-6">
            {getServiceAreaDisplay()}
          </p>
        </div>

        {/* Insurance */}
        {professional.insurance_accepted && professional.insurance_accepted.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-medium">Insurance Accepted</span>
            </div>
            <div className="flex flex-wrap gap-1 pl-6">
              {professional.insurance_accepted.slice(0, 3).map((insurance) => (
                <Badge key={insurance} variant="outline" className="text-xs">
                  {insurance}
                </Badge>
              ))}
              {professional.insurance_accepted.length > 3 && (
                <Badge variant="outline" className="text-xs">
                  +{professional.insurance_accepted.length - 3} more
                </Badge>
              )}
            </div>
          </div>
        )}

        {/* Specialties */}
        {professional.specialties && professional.specialties.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium">Specialties</h4>
            <div className="flex flex-wrap gap-1">
              {professional.specialties.slice(0, 4).map((specialty) => (
                <Badge key={specialty} variant="secondary" className="text-xs">
                  {specialty}
                </Badge>
              ))}
              {professional.specialties.length > 4 && (
                <Badge variant="secondary" className="text-xs">
                  +{professional.specialties.length - 4} more
                </Badge>
              )}
            </div>
          </div>
        )}

        {/* Areas of Expertise */}
        {professional.areas_of_expertise && professional.areas_of_expertise.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium">Areas of Expertise</h4>
            <div className="flex flex-wrap gap-1">
              {professional.areas_of_expertise.slice(0, 3).map((area) => (
                <Badge key={area} variant="outline" className="text-xs">
                  {area}
                </Badge>
              ))}
              {professional.areas_of_expertise.length > 3 && (
                <Badge variant="outline" className="text-xs">
                  +{professional.areas_of_expertise.length - 3} more
                </Badge>
              )}
            </div>
          </div>
        )}

        {/* Spacer for hover actions */}
        <div className="pt-4"></div>
      </CardContent>
      </Link>
      
        {/* Contact actions outside the link to prevent nested interactions */}
      <div className="absolute bottom-4 left-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-background/95 backdrop-blur-sm rounded-md p-2 border border-border/50">
        {professional.email ? (
          <Button 
            size="sm" 
            className="flex-1"
            asChild
          >
            <a href={`mailto:${professional.email}`} onClick={(e) => e.stopPropagation()}>
              <Mail className="w-4 h-4 mr-1" />
              {isCaregiver ? 'Contact' : 'Email'}
            </a>
          </Button>
        ) : (
          <Button size="sm" className="flex-1" disabled>
            <Mail className="w-4 h-4 mr-1" />
            {isCaregiver ? 'Contact' : 'Email'}
          </Button>
        )}
        
        {professional.phone && (
          <Button 
            variant="outline" 
            size="sm" 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              window.open(`tel:${professional.phone}`, '_blank');
            }}
          >
            <Phone className="w-4 h-4" />
          </Button>
        )}
        
        {(professional.website_url || professional.company_website) && (
          <Button 
            variant="outline" 
            size="sm" 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              window.open(professional.website_url || professional.company_website, '_blank');
            }}
          >
            <Globe className="w-4 h-4" />
          </Button>
        )}
      </div>
    </Card>
  );
}